import {
  Clickable,
  Icon,
  Image,
  Stack,
  IconButton,
  Lightbox,
} from '@fashionnovateam/nova-ui-toolkit';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PurchaseOrder } from '../../Lib/Types/purchase-order';

type PoImageProps = {
  purchaseOrder: PurchaseOrder;
  details?: boolean;
  isMobile?: boolean;
};

function getImageSize(details?: boolean, isMobile?: boolean) {
  if (details && isMobile)
    return 'min-h-[184px] h-[184px] min-w-[122px] w-[122px]';
  if (details) return 'h-auto max-h-[540px] w-full max-w-[360px] aspect-[2/3]';
  return 'min-h-[220px] h-[220px] min-w-[144px] w-[144px] md:min-h-[250px] md:h-[250px] md:min-w-[164px] md:w-[164px]';
}

export function PoImage({ purchaseOrder, details, isMobile }: PoImageProps) {
  const [imageIndex, setImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const allImages = useMemo(() => {
    const vendorImages = purchaseOrder.images ?? [];
    const marketplaceUrls = (purchaseOrder.marketplace_images ?? []).map(
      (img) => img.url,
    );
    return [...vendorImages, ...marketplaceUrls];
  }, [purchaseOrder.images, purchaseOrder.marketplace_images]);

  const imageCount = allImages.length;
  const safeIndex = imageCount > 0 ? Math.min(imageIndex, imageCount - 1) : 0;
  const currentImage = allImages[safeIndex];
  const imageSize = getImageSize(details, isMobile);

  const lightboxSlides = useMemo(
    () => allImages.map((src) => ({ src })),
    [allImages],
  );

  const handleLeftImage = useCallback(() => {
    setImageIndex((prev) => (prev === 0 ? imageCount - 1 : prev - 1));
  }, [imageCount]);

  const handleRightImage = useCallback(() => {
    setImageIndex((prev) => (prev + 1 >= imageCount ? 0 : prev + 1));
  }, [imageCount]);

  useEffect(() => {
    setImageIndex(0);
    setIsLightboxOpen(false);
  }, [purchaseOrder.po_number]);

  useEffect(() => {
    setImageIndex((prev) => (prev >= imageCount ? 0 : prev));
  }, [imageCount]);

  return (
    <div
      className={`relative bg-neutral-200 ${details ? 'rounded-xl' : 'rounded-tl-lg'} ${imageSize} flex items-center justify-center`}
    >
      {currentImage && !details && (
        <Link
          to={`purchase-orders/${purchaseOrder.po_number}`}
          style={{
            backgroundImage: `url(${currentImage})`,
            backgroundSize: '100% 100%',
            backgroundPosition: 'center',
          }}
          className="block w-full h-full border-r border-neutral-200"
        />
      )}
      {currentImage && details && (
        <Clickable
          onClick={() => setIsLightboxOpen(true)}
          className="w-full h-full p-0 border-0 bg-transparent"
        >
          <Image
            src={currentImage}
            alt="purchase order"
            className="object-cover w-full h-full rounded-xl"
          />
        </Clickable>
      )}
      {!currentImage && !details && (
        <Link
          to={`purchase-orders/${purchaseOrder.po_number}`}
          className="w-full h-full flex items-center justify-center"
        >
          <Icon
            name="photo-image/photo-image-cancel"
            className="size-6 text-base-disabled"
          />
        </Link>
      )}
      {!currentImage && details && (
        <Icon
          name="photo-image/photo-image-cancel"
          className="size-6 text-base-disabled"
        />
      )}
      {imageCount > 1 && (
        <Stack
          gap="xs"
          direction="row"
          className="absolute bottom-0 left-0 mb-2 ml-2"
        >
          <IconButton
            color="secondary"
            size="sm"
            label="image-left"
            onClick={handleLeftImage}
            icon={<Icon.ArrowLeft />}
          />
          <IconButton
            color="secondary"
            size="sm"
            label="image-rigth"
            onClick={handleRightImage}
            icon={<Icon.ArrowRight />}
          />
        </Stack>
      )}
      {details && (
        <Lightbox
          slides={lightboxSlides}
          index={safeIndex}
          isOpen={isLightboxOpen}
          onClose={() => setIsLightboxOpen(false)}
        />
      )}
    </div>
  );
}
